import { useMemo, useState } from "react";
import styles from "./ImageGeneratorTab.module.css";

const LOCAL_GENERATOR_URL = "http://localhost:8787";

const PROMPT_TEMPLATE = `Create a highly realistic vertical homemade recipe image for:

[RECIPE NAME]

IMPORTANT:
Use ONLY the recipe name as input.

Based on general culinary knowledge, automatically determine:
- the authentic main ingredients
- appropriate seasonings
- correct cooking vessel
- correct cooking method
- final color and texture
- appropriate garnish
- realistic finished appearance

Do not ask for additional recipe information.

IMAGE FORMAT:
Vertical 4:5 social-media recipe image.

COMPOSITION:
Divide the image horizontally into EXACTLY TWO large sections.

Use a thin clean white divider between the two sections.

==============================
TOP SECTION — RAW INGREDIENTS
==============================

Show the ingredients needed to make [RECIPE NAME] arranged clearly inside or around the cooking vessel that best fits the recipe.

Depending on the recipe, automatically choose an appropriate:

- black slow cooker
- crockpot
- Dutch oven
- skillet
- baking dish
- casserole dish
- sheet pan
- large cooking pot

The ingredients should still be raw or uncooked.

Arrange approximately 6–8 visually important ingredients in separate groups so each ingredient is easy to recognize.

Do NOT mix everything together.

Examples of presentation:
meat or seafood grouped on one side
vegetables grouped separately
potatoes or pasta grouped separately
cheese in a pile
seasonings in a small bowl
garlic in a small clear glass bowl
broth or sauce in a glass cup
butter in a small bowl

Use small transparent glass bowls for small or liquid ingredients where appropriate.

INGREDIENT LABELS:

Add a short ingredient label close to every major ingredient.

Label style:
- bold uppercase black text
- thick white outline
- subtle dark shadow
- condensed heavy social-media font
- very readable
- short ingredient names only
- labels positioned directly beside the correct ingredient
- labels must not cover important food details

Example label style:
SHRIMP
GARLIC
BUTTER
POTATOES
CHEDDAR
BROTH

Do NOT include measurements.

==============================
BOTTOM SECTION — FINISHED FOOD
==============================

Show [RECIPE NAME] fully cooked in the SAME cooking vessel or a visually consistent vessel.

The finished recipe should occupy most of the bottom frame.

Make it look rich, comforting, and homemade.

Show realistic details appropriate to the recipe such as:

- browned meat
- visible seasonings
- naturally melted cheese
- creamy sauce
- rustic chopped vegetables
- roasted edges
- bubbling broth
- juicy meat
- crispy surface
- herbs scattered naturally
- sauce clinging to ingredients
- steam when appropriate

The ingredients should remain recognizable.

Do not make the food look perfectly arranged.

Use a generous family-style portion.

=================================
HOMEMADE STYLE DIRECTION
=================================

The image must look like a delicious recipe made in a REAL HOME KITCHEN.

Visual style:
- homemade comfort food
- cozy everyday family meal
- viral Facebook recipe photo
- viral Pinterest recipe photo
- casual American home-cooking aesthetic
- warm kitchen atmosphere
- realistic home countertop
- simple cookware
- slightly imperfect ingredient arrangement
- generous portions
- natural food textures
- believable moisture and sauce
- natural browning
- realistic melted cheese
- visible seasoning
- casual garnish

The food should look appetizing because it looks REAL and homemade, not because it is perfectly styled.

LIGHTING:
Warm bright kitchen lighting.
Soft natural highlights.
No dramatic restaurant lighting.
No dark moody shadows.
No luxury studio setup.

CAMERA:
Slightly elevated angle, approximately 35–45 degrees.
Close enough that the food fills most of the frame.
Natural smartphone-style recipe photography.
Sharp food with slightly softer kitchen background.

COLOR:
Warm natural colors.
Rich but believable food color.
Avoid excessive saturation.
Avoid artificial orange/yellow color grading.

IMPORTANT:
The final result should look like someone cooked the recipe at home and photographed it for a popular recipe Facebook or Pinterest page.

AVOID:
fine-dining presentation
luxury restaurant plating
gourmet styling
tiny portions
perfect geometric placement
studio-white food photography
chef plating
expensive table settings
overly perfect symmetry
artificial plastic-looking food
excessive garnish
watermarks
logos

Do not add:
recipe instructions
measurements
recipe title
extra captions

Only ingredient labels are allowed in the top section.`;

const PROMPT_OPTIONS = [
  { id: "two-section", name: "Two Sections — Raw Ingredients + Finished Food", template: PROMPT_TEMPLATE },
  { id: "four-step", name: "Four-Step 2×2 Cooking Process", template: `Create a highly realistic vertical homemade cooking-process collage for:\n\n[RECIPE NAME]\n\nUse ONLY the recipe name as input. Automatically infer authentic ingredients, preparation, cooking method, cookware, final appearance, and serving style.\n\nFORMAT: Vertical 4:5 social-media image. Create EXACTLY FOUR equal photographic panels in a clean 2×2 grid with thin white divider lines. Keep the same kitchen, ingredients, cookware, and recipe consistent.\n\nTOP LEFT — INGREDIENTS: Show 5–8 important raw ingredients casually arranged on a real home-kitchen countertop in clear glass bowls, ceramic bowls, plates, and basic containers.\n\nTOP RIGHT — PREPARATION: Show a natural human hand performing the most recognizable preparation action for [RECIPE NAME], such as mixing, coating, pouring, seasoning, stuffing, rolling, cutting, breading, assembling, or brushing sauce.\n\nBOTTOM LEFT — COOKING: Show [RECIPE NAME] actively cooking with the correct method: frying, baking, air frying, sautéing, simmering, grilling, roasting, slow cooking, or boiling. Show believable steam, browning, bubbling, melting, caramelization, or crispy edges.\n\nBOTTOM RIGHT — FINISHED RECIPE: Show [RECIPE NAME] completely cooked in a simple home-style plate, bowl, tray, or baking dish. Make it generous, natural, comforting, and slightly imperfect.\n\nSTYLE: Real home kitchen, casual American comfort food, warm bright lighting, smartphone food photography, natural portions and textures. Avoid fine dining, luxury styling, perfect symmetry, commercial advertising, text, labels, watermarks, and logos. The story must clearly read: INGREDIENTS → PREPARATION → COOKING → FINISHED FOOD.` },
  { id: "crockpot", name: "Crockpot Ingredients + Creamy Finished Dish", template: `Create a photorealistic homemade social-media recipe image for:\n\n[RECIPE NAME]\n\nUse ONLY the recipe name as input. Automatically determine authentic ingredients, the correct slow-cooker preparation, final consistency, garnish, and finished appearance.\n\nFORMAT: Vertical 4:5. Divide the image horizontally into EXACTLY TWO large sections separated by a thin white line.\n\nTOP HALF — LABELED INGREDIENTS: Show a large black slow cooker or crockpot from a slightly elevated angle. Arrange 6–8 uncooked ingredients for [RECIPE NAME] in separate clear sections inside or around it. Use clear glass bowls for garlic, butter, broth, cream, sauce, seasoning, or other small ingredients. Add short bold uppercase ingredient labels with white outline and subtle shadow. No measurements.\n\nBOTTOM HALF — FINISHED CROCKPOT: Show the same crockpot filled with fully cooked [RECIPE NAME]. Represent the correct consistency: creamy sauce, naturally melted cheese, believable broth and steam, tender meat, or soft rustic potatoes as appropriate. Use only fitting herbs or simple toppings.\n\nSTYLE: Cozy real family slow-cooker dinner, normal household appliance, warm home-kitchen lighting, bright natural textures, generous portions, slightly imperfect arrangement. Avoid gourmet styling, restaurant plating, excessive garnish, artificial glossy food, logos, watermarks, recipe instructions, recipe title, and extra text. Only ingredient labels are allowed in the top section.` },
  { id: "title-sidebar", name: "Big Recipe Title + Finished Food + Ingredient Sidebar", template: `Create a vertical homemade recipe infographic for:\n\n[RECIPE NAME]\n\nUse ONLY the recipe name as input. Automatically determine the finished dish, four most recognizable ingredients, serving vessel, colors, and textures.\n\nFORMAT: Vertical 4:5 with a clean white background.\n\nTOP TITLE: Place [RECIPE NAME] across the top 15–20% in VERY LARGE heavy bold black uppercase condensed typography, centered. No subtitle.\n\nLEFT SIDE: Below the title, use 68–72% width for a large close-up photograph of generous finished [RECIPE NAME] on an appropriate everyday plate, bowl, baking dish, tray, or skillet. Show crispy edges, bubbling cheese, juicy filling, soft bread, crumb texture, thick sauce, frosting, or golden crust when appropriate.\n\nRIGHT SIDE: Use 28–32% width for a vertical column with EXACTLY FOUR important ingredients from [RECIPE NAME], each in an individual transparent glass bowl with a bold black uppercase short label. No quantities.\n\nSTYLE: Homemade comfort food, casual kitchen plating, natural uneven shapes, realistic crumbs and melting, bright natural home lighting, simple plates and bowls. Avoid fine dining, luxury plates, tiny portions, microgreens, perfect symmetry, commercial studio styling, watermarks, logos, instructions, measurements, and any text except the title and four ingredient labels.` },
  { id: "slow-cooker-story", name: "2×2 Slow-Cooker Story", template: `Create a highly realistic homemade vertical cooking collage for:\n\n[RECIPE NAME]\n\nUse ONLY the recipe name as input. Automatically determine authentic ingredients, cookware, preparation, cooking liquid, seasoning, finished appearance, garnish, and serving style.\n\nFORMAT: Vertical 4:5. Create EXACTLY FOUR panels in a clean 2×2 grid with thin white divider lines. Keep the same recipe, kitchen, and cookware consistent.\n\nTOP LEFT — INGREDIENT SETUP: Show 6–9 ingredients for [RECIPE NAME] on a real home counter with a slow cooker, Dutch oven, pot, or skillet in the background.\n\nTOP RIGHT — ADDING INGREDIENTS: Show natural human hands adding or pouring broth, cream, sauce, seasoning, cheese, or tomatoes into the vessel. Ingredients remain uncooked and visibly separated.\n\nBOTTOM LEFT — COOKED IN THE POT: Show fully cooked [RECIPE NAME] in the same vessel with steam when appropriate, a wooden spoon, tender ingredients, thickened sauce, broth, melted cheese, and herbs as suitable.\n\nBOTTOM RIGHT — SERVED BOWL: Show a generous comforting serving in a simple ceramic bowl or plate with an appropriate casual garnish.\n\nSTYLE: Real everyday home kitchen, warm bright light, smartphone photography, rustic chopping, natural textures, normal cookware, slightly imperfect presentation. Avoid restaurant style, gourmet presentation, commercial ads, perfect symmetry, tiny portions, luxury dinnerware, artificial steam, logos, watermarks, and all text.` },
  { id: "soup-stew", name: "Soup / Stew 2×2 with Ladle Close-Up", template: `Create a realistic homemade 2×2 recipe-process collage for:\n\n[RECIPE NAME]\n\nUse ONLY the recipe name as input. Infer authentic soup or stew ingredients, protein, vegetables, beans or starches, broth or sauce, seasoning, consistency, vessel, garnish, and serving style.\n\nFORMAT: Vertical 4:5 with EXACTLY FOUR panels and thin white dividers.\n\nTOP LEFT — INGREDIENTS: Show 6–9 recognizable ingredients casually arranged on a home counter in glass bowls, ceramic bowls, opened generic cans, chopped vegetables, protein, seasoning, broth, or sauce.\n\nTOP RIGHT — LOADING THE POT: Show a black slow cooker, Dutch oven, or pot filled with separated raw ingredients while hands pour broth, tomato sauce, cream, stock, or seasoned liquid and add seasoning.\n\nBOTTOM LEFT — SIMMERING FINISHED FOOD: Show fully cooked [RECIPE NAME] in the same vessel, with realistic steam, a wooden spoon or ladle, thick rustic broth, and identifiable meat, vegetables, beans, potatoes, corn, pasta, or tomatoes.\n\nBOTTOM RIGHT — LADLE CLOSE-UP: Show a large ladle lifting a generous scoop close to the camera, with multiple ingredients visible and the pot softly blurred behind it.\n\nSTYLE: Cozy hearty family dinner, warm bright natural kitchen light, smartphone close-up, real ingredients, imperfect cuts, natural herbs and seasoning. Avoid restaurant soup styling, fine dining, perfect cubes, microgreens, luxury tableware, artificial broth, logos, watermarks, labels, title, instructions, and text.` },
  { id: "detailed-collage", name: "Raw + Cooked + Ingredient Strip + Bowl + Spoon", template: `Create a highly detailed vertical homemade recipe collage for:\n\n[RECIPE NAME]\n\nUse ONLY the recipe name as input. Automatically determine authentic ingredients, correct cookware, cooking method, final consistency, toppings, garnish, and serving appearance.\n\nFORMAT: Vertical 3:4 or 4:5. Follow this composition exactly.\n\nTOP SECTION: Split the top 48% into two equal vertical panels with a thin white divider. TOP LEFT shows the main raw ingredients for [RECIPE NAME] separated inside a Dutch oven, slow cooker, pot, or deep skillet. TOP RIGHT shows the same vessel after cooking with everything naturally mixed, a wooden spoon, rich sauce, broth, cheese, vegetables, meat, beans, or pasta as appropriate.\n\nMIDDLE INGREDIENT STRIP: Create a narrow horizontal strip with 5–7 important ingredients in generic unbranded cans, packets, jars, or fresh containers. Add subtle white cutout-style outlines and simple readable ingredient descriptions such as BLACK BEANS, CORN, DICED TOMATOES, CHICKEN, or SEASONING. Never copy logos.\n\nBOTTOM SECTION: Split into two vertical panels. BOTTOM LEFT shows a generous casual bowl or plate of [RECIPE NAME] with authentic toppings. BOTTOM RIGHT shows an extreme close-up of a spoon or fork lifting a satisfying bite with recognizable ingredients and the bowl softly blurred behind it.\n\nSTYLE: Viral homemade Facebook/Pinterest comfort-food photography, real kitchen, warm natural light, simple plates, hearty portions, rustic textures, realistic moisture and browning, natural close-up smartphone perspective. Avoid fine dining, perfect symmetry, luxury tableware, microgreens, studio backgrounds, commercial styling, plastic-looking food, watermarks, logos, recipe title, instructions, and measurements.` },
];

export function ImageGeneratorTab() {
  const [recipeName, setRecipeName] = useState("Chocolate Lava Cakes");
  const [selectedTemplateId, setSelectedTemplateId] = useState("two-section");
  const [aspectRatio, setAspectRatio] = useState("3:4");
  const [size, setSize] = useState("1200x1600");
  const [style, setStyle] = useState("photorealistic-natural");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [batchNames, setBatchNames] = useState("");
  const [batchUrls, setBatchUrls] = useState<Array<{ name: string; url: string }>>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedTemplate = PROMPT_OPTIONS.find((option) => option.id === selectedTemplateId) || PROMPT_OPTIONS[0]!;
  const prompt = useMemo(() => selectedTemplate.template.replace(/\[RECIPE NAME\]/g, recipeName.trim() || "[RECIPE NAME]").replace(/Vertical 4:5/g, `Vertical ${aspectRatio}`), [selectedTemplate, recipeName, aspectRatio]);

  async function generate() {
    if (!recipeName.trim() || isGenerating) return;
    setIsGenerating(true);
    setError(null);
    setImageUrl(null);
    try {
      const response = await fetch(`${LOCAL_GENERATOR_URL}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, size, style }),
      });
      const data = await response.json() as { url?: string; error?: string };
      if (!response.ok || !data.url) throw new Error(data.error || "Image generation failed");
      setImageUrl(`${data.url}?t=${Date.now()}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Image generation failed");
    } finally {
      setIsGenerating(false);
    }
  }

  async function generateBatch() {
    const names = batchNames.split("\n").map((name) => name.trim()).filter(Boolean).slice(0, 20);
    if (!names.length || isGenerating) return;
    setIsGenerating(true);
    setError(null);
    setBatchUrls([]);
    try {
      const response = await fetch(`${LOCAL_GENERATOR_URL}/generate-batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: names.map((name) => ({ prompt: selectedTemplate.template.replace(/\[RECIPE NAME\]/g, name).replace(/Vertical 4:5/g, `Vertical ${aspectRatio}`), size, style })) }),
      });
      const data = await response.json() as { results?: Array<{ filename: string; url: string }>; error?: string };
      if (!response.ok || !data.results) throw new Error(data.error || "Batch generation failed");
      setBatchUrls(data.results.map((result, index) => ({ name: names[index] || result.filename, url: `${result.url}?t=${Date.now()}-${index}` })));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Batch generation failed");
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div><h1>Recipe Image Generator</h1><p>Enter a recipe name. The full recipe-image prompt is filled automatically.</p></div>
        <span className={styles.localBadge}>Local Codex Image Gen</span>
      </div>
      <div className={styles.layout}>
        <section className={styles.formCard}>
          <label>Prompt style</label>
          <select className={styles.templateSelect} value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
            {PROMPT_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select>
          <label>Recipe name</label>
          <input className={styles.recipeName} value={recipeName} onChange={(e) => setRecipeName(e.target.value)} placeholder="Example: Chicken Pot Pie" />
          <label>Generated prompt preview</label>
          <textarea value={prompt} readOnly rows={12} />
          <div className={styles.row}>
            <div><label>Aspect ratio</label><select value={aspectRatio} onChange={(e) => { const ratio = e.target.value; setAspectRatio(ratio); setSize(ratio === "3:4" ? "1200x1600" : ratio === "4:5" ? "1200x1500" : "1024x1024"); }}><option value="3:4">3:4 portrait</option><option value="4:5">4:5 portrait</option><option value="1:1">1:1 square</option></select></div>
            <div><label>Style</label><select value={style} onChange={(e) => setStyle(e.target.value)}><option value="photorealistic-natural">Photorealistic natural</option><option value="editorial-food">Editorial food</option><option value="bright-studio">Bright studio</option><option value="rustic-home">Rustic home</option></select></div>
          </div>
          <button className={styles.generateButton} onClick={generate} disabled={isGenerating || !recipeName.trim()}>{isGenerating ? "Generating…" : "Generate recipe image"}</button>
          <div className={styles.batchBox}>
            <label>Batch recipes (one recipe name per line, maximum 20)</label>
            <textarea value={batchNames} onChange={(e) => setBatchNames(e.target.value)} rows={5} placeholder={"Chicken Pot Pie\nBeef Stew\nChocolate Lava Cakes"} />
            <button className={styles.batchButton} onClick={generateBatch} disabled={isGenerating || !batchNames.trim()}>{isGenerating ? "Generating batch…" : "Generate batch"}</button>
          </div>
          {error && <div className={styles.error}>{error}</div>}
          <p className={styles.help}>Run <code>python scripts/image_gen_server.py</code> from the project root before generating.</p>
        </section>
        <section className={styles.previewCard}>
          <h2>Preview</h2>
          {batchUrls.length > 0 ? <div className={styles.batchGrid}>{batchUrls.map((item) => <div key={item.url} className={styles.batchItem}><img src={item.url} alt={`Generated ${item.name} recipe`} className={styles.preview} /><strong>{item.name}</strong><a className={styles.download} href={item.url} download={`${item.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`}>Download PNG</a></div>)}</div> : imageUrl ? <><img src={imageUrl} alt={`Generated ${recipeName} recipe`} className={styles.preview} /><a className={styles.download} href={imageUrl} download="recipe-image.png">Download PNG</a></> : <div className={styles.empty}>{isGenerating ? "Waiting for generation…" : "Your generated image will appear here."}</div>}
        </section>
      </div>
    </div>
  );
}
